// One dialog for the quick actions on a signup or trial: decline, ask for
// more info, send a trial reminder, extend a trial. Each posts to its own
// endpoint and reports back through `notify`.
import { useEffect, useRef, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import api from '../../api/axios'
import type { SignupRow } from './types'
import { errMsg, shortDate } from './types'

export type ActionKind = 'decline' | 'ask' | 'remind' | 'extend'

const TITLES: Record<ActionKind, string> = {
  decline: 'Decline signup',
  ask: 'Ask for more information',
  remind: 'Send a trial reminder',
  extend: 'Extend the trial',
}

export default function ActionDialog({ kind, row, onClose, onDone, notify }: {
  kind: ActionKind
  row: SignupRow
  onClose: () => void
  onDone: () => void
  notify: (msg: string, tone?: 'ok' | 'error') => void
}) {
  const first = (row.contact.name || '').split(' ')[0] || 'there'
  const [reason, setReason] = useState('')
  const [message, setMessage] = useState(() => {
    if (kind === 'ask') return `Hi ${first},\n\nThanks for signing up ${row.companyName} for CITS Time. Before we switch your workspace on, could you tell us a little more about your company — what kind of work your crews do and roughly how many people will use the app?\n\nJust reply to this email.`
    if (kind === 'decline') return `Hi ${first},\n\nThanks for your interest. We weren't able to approve the trial for ${row.companyName}. If you think we got this wrong, just reply to this email and we'll take another look.`
    if (kind === 'remind') return `Hi ${first},\n\nJust a heads-up: your trial for ${row.companyName}${row.trialEndsAt ? ` ends on ${shortDate(row.trialEndsAt)}` : ' is ending soon'}. Everything you've set up stays put when you keep going — reply to this email and we'll get you switched over, or ask for a few more days if you need them.`
    return ''
  })
  const [sendEmail, setSendEmail] = useState(true)
  const [days, setDays] = useState(14)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const firstField = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null)

  useEffect(() => {
    firstField.current?.focus()
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      if (kind === 'decline') {
        if (!reason.trim()) { setError('Give a reason for the record.'); setBusy(false); return }
        await api.post(`/super-admin/signups/${row.id}/decline`, { reason, message, sendEmail })
        notify(`${row.companyName} declined${sendEmail ? ' — they have been emailed' : ''}`)
      } else if (kind === 'ask') {
        await api.post(`/super-admin/signups/${row.id}/request-info`, { message })
        notify(`Question sent to ${row.contact.email}`)
      } else if (kind === 'remind') {
        await api.post(`/super-admin/signups/${row.id}/remind`, { message })
        notify(`Reminder sent to ${row.contact.email}`)
      } else {
        await api.post(`/super-admin/companies/${row.id}/extend-trial`, { days })
        notify(`${row.companyName}'s trial extended by ${days} days`)
      }
      onDone()
      onClose()
    } catch (err) {
      setError(errMsg(err, 'That didn\'t go through. Try again.'))
    } finally {
      setBusy(false)
    }
  }

  const input = 'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden="true" />
      <div role="dialog" aria-modal="true" aria-labelledby="action-title" className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200">
          <div>
            <h2 id="action-title" className="text-base font-semibold text-slate-900">{TITLES[kind]}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{row.companyName} · {row.contact.email}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" aria-label="Close"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          {kind === 'decline' && (
            <div className="space-y-1">
              <label htmlFor="ad-reason" className="text-xs font-semibold text-slate-700">Reason (kept in the audit log, not emailed)</label>
              <input id="ad-reason" ref={(el) => { firstField.current = el }} className={input} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Looks like a test / competitor / spam" />
            </div>
          )}
          {kind === 'extend' ? (
            <div className="space-y-1">
              <label htmlFor="ad-days" className="text-xs font-semibold text-slate-700">Add days</label>
              <div className="flex gap-2 flex-wrap">
                {[7, 14, 30, 60].map((d) => (
                  <button key={d} type="button" onClick={() => setDays(d)} aria-pressed={days === d} className={`px-3 h-8 rounded-lg text-sm font-semibold border ${days === d ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-300 text-slate-700'}`}>{d}</button>
                ))}
                <input id="ad-days" ref={(el) => { firstField.current = el }} type="number" min={1} max={365} value={days} onChange={(e) => setDays(Math.max(1, Math.min(365, Number(e.target.value) || 1)))} className={`${input} w-24`} />
              </div>
              <p className="text-xs text-slate-500">Currently ends {shortDate(row.trialEndsAt)}. A suspended trial is switched back on.</p>
            </div>
          ) : (
            <div className="space-y-1">
              <label htmlFor="ad-msg" className="text-xs font-semibold text-slate-700">{kind === 'decline' ? 'Email to them' : 'Message'}</label>
              <textarea id="ad-msg" ref={kind === 'decline' ? undefined : (el) => { firstField.current = el }} rows={7} className={`${input} resize-y`} value={message} onChange={(e) => setMessage(e.target.value)} disabled={kind === 'decline' && !sendEmail} />
              <p className="text-xs text-slate-500">Sent from no-reply@citstime.app. {kind === 'ask' ? 'Replies come to you.' : 'Replies go to the support address.'}</p>
            </div>
          )}
          {kind === 'decline' && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} className="w-4 h-4" />
              Email them that it was declined
            </label>
          )}
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
          <button type="button" onClick={onClose} className="px-3 h-9 text-sm rounded-lg border border-slate-300 bg-white hover:bg-slate-50">Cancel</button>
          <button type="button" onClick={submit} disabled={busy} className={`px-3 h-9 text-sm font-semibold rounded-lg text-white disabled:opacity-50 ${kind === 'decline' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {busy ? 'Working…' : kind === 'decline' ? 'Decline' : kind === 'extend' ? `Extend by ${days} days` : 'Send'}
          </button>
        </div>
      </div>
    </div>
  )
}
